/* ───────────────────────────────────────────────────────────────
   ShopOwnerDashboard.jsx · 11 Jul 2025  (rev F – Safari-safe)
   — Uses http-only cookie + optional Bearer via http wrapper
   — Real-time “new-booking” + “booking-updated” (socket + 8s poll)
   — Calendar auto-refresh after accept/decline/complete/cancel
   — Uses http wrapper + SOCKET_URL
   — Updated: Clickable calendar icons, no rectangle buttons
   ─────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../contexts/I18n';
import { io } from 'socket.io-client';
import { FaBars } from 'react-icons/fa';
import { http, SOCKET_URL } from '../lib/http';
import { createImageHandler } from '../utils/imageHandler';
import { playNewBookingSound, prepareNewBookingSound } from '../utils/newBookingSound';
import {
  Sidebar,
  LoginView,
  CalendarTab,
  HistoryTab,
  AnalyticsTab,
  ProfileView,
  ProfileEditForm,
} from './shopOwnerDashboard/index.js';
import { AuthNavbar } from './Navbar';
import Footer from './Footer';

import './ShopOwnerDashboard.css';

/* ╔═══════════════ ROOT COMPONENT ═══════════════╗ */
export default function ShopOwnerDashboard({ generatedAccount }) {
  const { t } = useI18n();
  
  /* ───────── AUTH ───────── */
  const [cred, setCred] = useState({
    shopName: generatedAccount?.shopName ?? '',
    password: generatedAccount?.password ?? '',
  });
  const [logged, setLogged] = useState(false);
  const [logErr, setLErr] = useState('');
  

  /* ───────── DATA ───────── */
  const [biz, setBiz] = useState(null);
  const [appts, setApt] = useState([]);
  const [pulse, setPul] = useState(0); // calendar refresh trigger

  /* availability editor */
  const [avail, setAvail] = useState({});
  const [daySel, setDaySel] = useState('');

  /* profile-edit form */
  const [edit, setEdit] = useState(false);
  const [frm, setFrm] = useState({
    name: '', town: '', serviceCategory: '', phone: '', mapEmbed: '',
    imageFiles: [], portfolioImages: [], services: [{ serviceName: '', price: '', duration: '60' }],
    staff: [{ name: '', id: Date.now() }],
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [portfolioPreviews, setPortfolioPreviews] = useState([]);
  const [existingImageCount, setExistingImageCount] = useState(0);
  const [existingPortfolioCount, setExistingPortfolioCount] = useState(0);
  /* URLs to keep when user deletes some (sent to API so deletions persist) */
  const [existingGalleryUrlsToKeep, setExistingGalleryUrlsToKeep] = useState([]);
  const [existingCoverRemoved, setExistingCoverRemoved] = useState(false);
  const [existingPortfolioUrlsToKeep, setExistingPortfolioUrlsToKeep] = useState([]);
  const [fErr, setFE] = useState('');
  const [msg, setMsg] = useState('');

  /* ui */
  const [tab, setTab] = useState('calendar');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const pendingCount = useMemo(
    () =>
      appts.filter((a) => String(a?.status || '').toLowerCase() === 'pending').length,
    [appts],
  );
  const [tempPassword, setTempPassword] = useState(generatedAccount?.password || '');

  /* ───────── image handler ───────── */
  const imageHandler = useMemo(() => createImageHandler('shop'), []);

  /* ───────── login handlers ───────── */
  const onCred = e => setCred(c => ({ ...c, [e.target.name]: e.target.value }));

  const doLogin = async e => {
    e.preventDefault();
    prepareNewBookingSound();
    setLErr('');
    try {
      console.log('=== LOGIN DEBUG ===');
      console.log('Attempting login with:', cred.shopName.trim());
      
      const data = await http.postJson('/api/shopowner/login', {
        shopName: cred.shopName.trim(),
        password: cred.password,
      });
      
      console.log('Login response data:', data);
      
      // Check if login was successful
      if (data?.error) {
        console.log('Login failed:', data.error);
        return setLErr(data.error);
      }
      
      // If backend returns an access token, store it (works in Safari)
      if (data?.accessToken) {
        console.log('Storing access token:', data.accessToken);
        http.setToken(data.accessToken, { remember: true });
        if (data?.refreshToken) {
          http.setRefreshToken(data.refreshToken, { remember: true });
        }
      } else {
        console.log('No access token in response');
        return setLErr('Login failed - no token received');
      }
      
      console.log('Setting logged to true');
      setLogged(true);
      console.log('==================');
    } catch (err) {
      console.log('Login error:', err);
      setLErr(err.message || t('ownerLoginFailed'));
    }
  };


  const logout = () =>
    http.fetch('/api/shopowner/logout', { method: 'POST' })
      .finally(() => { http.clearToken?.(); window.location.assign('/'); });

  /* ───────── after login: load business + appointments ───────── */
  useEffect(() => {
    if (!logged) return;
    
    const loadData = async () => {
      try {
        // business
        const bizResp = await http.getJson(
          `/businesses?name=${encodeURIComponent(cred.shopName.trim())}&limit=1&page=1`,
        );
        const one = Array.isArray(bizResp?.data) ? bizResp.data[0] : null;
        setBiz(one);

        if (one) {
          setAvail(one.availability || {});
          setDaySel(Object.keys(one.availability || {})[0] || '');

          setFrm({
            name: one.name || '',
            town: one.town || '',
            serviceCategory: one.serviceCategory || '',
            phone: one.phone || '',
            mapEmbed: one.mapEmbed || '',
            imageFiles: [],
            portfolioImages: [],
            services: one.services?.length ? one.services.map(s => ({ 
              serviceName: s.serviceName || '', 
              price: s.price || '', 
              duration: s.duration || '60' 
            })) : [{ serviceName: '', price: '', duration: '60' }],
            staff: one.staff?.length ? one.staff.map(s => ({ 
              name: s.name || '',
              id: s._id || Date.now()
            })) : [{ name: '', id: Date.now() }],
          });
          
          // Load existing business images - combine imageUrl + gallery
          const allImages = [];
          if (one.imageUrl) {
            allImages.push(one.imageUrl);
          }
          if (one.gallery && Array.isArray(one.gallery)) {
            allImages.push(...one.gallery);
          }
          setImagePreviews(allImages.map(img => imageHandler.getUrl(img)));
          setExistingImageCount(allImages.length);
          setExistingCoverRemoved(false);
          setExistingGalleryUrlsToKeep(Array.isArray(one.gallery) ? [...one.gallery] : []);

          // Load portfolio images
          if (one.portfolio && Array.isArray(one.portfolio)) {
            setPortfolioPreviews(one.portfolio.map(img => imageHandler.getUrl(img)));
            setExistingPortfolioCount(one.portfolio.length);
            setExistingPortfolioUrlsToKeep([...one.portfolio]);
          } else {
            setPortfolioPreviews([]);
            setExistingPortfolioCount(0);
            setExistingPortfolioUrlsToKeep([]);
          }
        }

        // appointments
        const a = await http.getJson('/api/shopowner/appointments');
        setApt(Array.isArray(a) ? a : a?.data || []);
      } catch (error) {
        // Error loading dashboard data
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-run on logged/pulse; cred.shopName/imageHandler would trigger on every keystroke
  }, [logged, pulse]);

  /* Keep the appointment list live even if sockets fail (Netlify / Safari). */
  useEffect(() => {
    if (!logged) return undefined;

    const fetchAppointments = async () => {
      try {
        const a = await http.getJson('/api/shopowner/appointments');
        const next = Array.isArray(a) ? a : a?.data || [];
        setApt((prev) => {
          if (prev.length) {
            const prevIds = new Set(prev.map((x) => String(x._id)));
            const hasNewPending = next.some(
              (x) => !prevIds.has(String(x._id)) && String(x.status || '').toLowerCase() === 'pending',
            );
            if (hasNewPending) {
              playNewBookingSound();
              setTab('history');
            }
          }
          return next;
        });
      } catch {
        // keep the current list
      }
    };

    fetchAppointments();
    const tick = setInterval(fetchAppointments, 8000);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchAppointments();
    };
    window.addEventListener('focus', fetchAppointments);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(tick);
      window.removeEventListener('focus', fetchAppointments);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [logged]);

  /* Unlock Web Audio after login (browsers block sound without a prior user gesture). */
  useEffect(() => {
    if (!logged) return undefined;
    prepareNewBookingSound();
    const unlock = () => prepareNewBookingSound();
    window.addEventListener('pointerdown', unlock, { once: true, capture: true });
    return () => window.removeEventListener('pointerdown', unlock, { capture: true });
  }, [logged]);

  /* ───────── realtime sockets ───────── */
  useEffect(() => {
    if (!logged) return undefined;

    const upsert = (b) => {
      if (!b?._id) return;
      const id = String(b._id);
      setApt((arr) => {
        const i = arr.findIndex((x) => String(x._id) === id);
        return i === -1 ? [b, ...arr] : arr.map((x) => (String(x._id) === id ? { ...x, ...b } : x));
      });
    };

    const socket = io(SOCKET_URL, {
      withCredentials: true,
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      auth: (cb) => cb({ token: http.getToken?.() || '' }),
    });

    const onCreated = (b) => {
      upsert(b);
      if (b?.status === 'pending') {
        playNewBookingSound();
        setTab('history');
      }
    };

    socket.on('booking:created', onCreated);
    socket.on('booking:updated', upsert);
    return () => {
      socket.off('booking:created', onCreated);
      socket.off('booking:updated', upsert);
      socket.disconnect();
    };
  }, [logged]);

  /* ───────── appointment mutations ───────── */
  const setStatus = async (id, status) => {
    const r = await http.fetch(`/api/shopowner/appointments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!r.ok) return;
    const up = await r.json();
    setApt(arr => arr.map(a => (a._id === id ? up : a)));
    setPul(p => p + 1); // refresh calendar grid
  };

  /* ═════════ AVAILABILITY HELPERS ═════════ */
  const toggleDay = d => {
    setAvail(a => {
      const copy = { ...a };
      if (copy[d]) delete copy[d]; else copy[d] = [];
      return copy;
    });
    if (daySel === d) setDaySel('');
  };
  const selectDay = d => setDaySel(d);
  const toggleSlot = s => {
    if (!daySel) return;
    setAvail(a => {
      const lst = a[daySel] || [];
      return { ...a, [daySel]: lst.includes(s) ? lst.filter(x => x !== s) : [...lst, s] };
    });
  };

  /* ═════════ PROFILE FORM HELPERS ═════════ */
  const onF = e => setFrm(f => ({ ...f, [e.target.name]: e.target.value }));
  const onSrv = (i, e) => {
    const { name: k, value: v } = e.target;
    setFrm(f => ({
      ...f,
      services: f.services.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));
  };
  const addSrv = () =>
    setFrm(f => ({ ...f, services: [...f.services, { serviceName: '', price: '', duration: '60' }] }));
  const rmSrv = i =>
    setFrm(f => ({ ...f, services: f.services.filter((_, idx) => idx !== i) }));

  /* ═════════ STAFF FORM HELPERS ═════════ */
  const onStaff = (i, e) => {
    const { name: k, value: v } = e.target;
    setFrm(f => ({
      ...f,
      staff: f.staff.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));
  };
  const addStaff = () =>
    setFrm(f => ({ 
      ...f, 
      staff: [...f.staff, { 
        name: '', 
        photoUrl: '', 
        photoFile: null,
        id: Date.now() + Math.random() // Add unique ID to prevent mixing
      }] 
    }));
  const rmStaff = i =>
    setFrm(f => ({ ...f, staff: f.staff.filter((_, idx) => idx !== i) }));
  const onFile = e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const MAX_IMAGES = 30;
    if (files.length + frm.imageFiles.length > MAX_IMAGES) {
      setFE(`You can upload up to ${MAX_IMAGES} images.`);
      return;
    }
    
    setFE('');
    setFrm(f => ({ ...f, imageFiles: [...f.imageFiles, ...files] }));
    // Add new file previews to existing ones
    setImagePreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removeImage = idx => {
    if (idx < existingImageCount) {
      // Existing image: remove from previews and track for API (so deletion persists on save)
      if (idx === 0) {
        setExistingCoverRemoved(true);
      } else {
        setExistingGalleryUrlsToKeep(prev => prev.filter((_, i) => i !== idx - 1));
      }
      setImagePreviews(prev => {
        const copy = [...prev];
        copy.splice(idx, 1);
        return copy;
      });
      setExistingImageCount(prev => prev - 1);
    } else {
      // This is a new upload - remove from both files and previews
      const newUploadIdx = idx - existingImageCount;
      setFrm(f => {
        const copy = [...f.imageFiles];
        copy.splice(newUploadIdx, 1);
        return { ...f, imageFiles: copy };
      });
      setImagePreviews(prev => {
        const copy = [...prev];
        copy.splice(idx, 1);
        return copy;
      });
    }
  };

  // Portfolio image handlers
  const handlePortfolioFileChange = e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const MAX_PORTFOLIO_IMAGES = 30;
    if (files.length + frm.portfolioImages.length > MAX_PORTFOLIO_IMAGES) {
      setFE(t('youCanUploadUpTo10PortfolioImages') || `You can upload up to ${MAX_PORTFOLIO_IMAGES} portfolio images.`);
      return;
    }
    
    setFE('');
    setFrm(f => ({ ...f, portfolioImages: [...f.portfolioImages, ...files] }));
    setPortfolioPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removePortfolioImage = idx => {
    if (idx < existingPortfolioCount) {
      // Existing portfolio image: remove from previews and track for API (so deletion persists on save)
      setExistingPortfolioUrlsToKeep(prev => prev.filter((_, i) => i !== idx));
      setPortfolioPreviews(prev => {
        const copy = [...prev];
        copy.splice(idx, 1);
        return copy;
      });
      setExistingPortfolioCount(prev => prev - 1);
    } else {
      // This is a new upload - remove from both files and previews
      const newUploadIdx = idx - existingPortfolioCount;
      setFrm(f => {
        const copy = [...f.portfolioImages];
        copy.splice(newUploadIdx, 1);
        return { ...f, portfolioImages: copy };
      });
      setPortfolioPreviews(prev => {
        const copy = [...prev];
        copy.splice(idx, 1);
        return copy;
      });
    }
  };


  const saveProfile = async e => {
    e.preventDefault();
    setFE(''); setMsg('');
    if (!frm.name || !frm.town || !frm.serviceCategory ||
      !frm.phone || !frm.mapEmbed ||
      frm.services.some(s => !s.serviceName || !s.price || !s.duration) ||
      frm.staff.some(s => !s.name) ||
      !Object.keys(avail).length ||
      Object.values(avail).some(lst => !lst.length))
      return setFE(t('ownerPleaseComplete'));

    const fd = new FormData();
    
    // Handle staff data
    const staffData = frm.staff.map(s => ({
      name: s.name
    }));
    
    Object.entries(frm).forEach(([k, v]) => {
      if (k === 'imageFiles' || k === 'portfolioImages' || k === 'staff') return;
      fd.append(k, k === 'services' ? JSON.stringify(v) : v);
    });
    
    // Add staff data
    fd.append('staff', JSON.stringify(staffData));

    // Tell API which existing images to keep (so deletions persist)
    if (existingCoverRemoved) fd.append('removeCover', 'true');
    fd.append('existingGalleryUrls', JSON.stringify(existingGalleryUrlsToKeep));
    fd.append('existingPortfolioUrls', JSON.stringify(existingPortfolioUrlsToKeep));
    
    // Add business images (new uploads)
    frm.imageFiles.forEach(file => {
      fd.append('imageFiles', file);
    });
    
    // Add portfolio images (new uploads)
    frm.portfolioImages.forEach(file => {
      fd.append('portfolioImages', file);
    });
    
    fd.append('availability', JSON.stringify(avail));

    console.log('=== AUTHENTICATION DEBUG ===');
    console.log('Token available:', !!http.getToken());
    console.log('Token value:', http.getToken());
    console.log('User logged in:', logged);
    console.log('Shop name:', cred.shopName);
    console.log('FormData contents:', Array.from(fd.entries()));
    console.log('============================');
    
    const r = await http.fetch('/api/shopowner/business', { method: 'PUT', body: fd });
    console.log('Response status:', r.status);
    console.log('Response ok:', r.ok);
    
    if (!r.ok) {
      const errorText = await r.text();
      console.log('Error response:', errorText);
      let msg = errorText;
      try {
        const parsed = JSON.parse(errorText);
        if (parsed && typeof parsed.message === 'string') msg = parsed.message;
      } catch {
        /* keep raw text */
      }
      setFE(msg);
      return;
    }
    const up = await r.json();
    setBiz(up); setEdit(false); setMsg(t('profileUpdated'));
  };

  /* ───────── RENDER ───────── */
  if (!logged) {
    return (
      <>
        <AuthNavbar />
        <LoginView
          generatedAccount={generatedAccount}
          logErr={logErr}
          cred={cred}
          onCred={onCred}
          doLogin={doLogin}
        />
        <Footer />
      </>
    );
  }

  return (
    <div className="dashboard-container" data-active-tab={tab}>
      <Sidebar 
        tab={tab} 
        setTab={setTab} 
        logout={logout} 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        pendingCount={pendingCount}
        badgeCounts={{ history: pendingCount }}
      />
      <header className={`banner ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
        {!sidebarOpen && (
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
            title="Open sidebar"
            type="button"
          >
            <FaBars />
          </button>
        )}
        <span className="welcome-text">
          {t('ownerWelcomeBack')}{' '}
          {biz?.name ? <strong className="user-name">{biz.name}</strong> : null}
        </span>
      </header>
      <main className="content-area">
        {!biz ? (<p style={{ padding: '1rem' }}>{t('ownerLoading')}</p>) : (
          (() => {
            switch (tab) {
              case 'calendar': return <CalendarTab appts={appts} biz={biz} />;
              case 'history': return <HistoryTab appts={appts} updateStatus={setStatus} />;
              case 'profile':
                return edit ? (
                  <ProfileEditForm
                    frm={frm}
                    onF={onF}
                    saveProfile={saveProfile}
                    fErr={fErr}
                    msg={msg}
                    setEdit={setEdit}
                    imagePreviews={imagePreviews}
                    removeImage={removeImage}
                    onFile={onFile}
                    imageHandler={imageHandler}
                    portfolioPreviews={portfolioPreviews}
                    removePortfolioImage={removePortfolioImage}
                    handlePortfolioFileChange={handlePortfolioFileChange}
                    onSrv={onSrv}
                    rmSrv={rmSrv}
                    addSrv={addSrv}
                    onStaff={onStaff}
                    rmStaff={rmStaff}
                    addStaff={addStaff}
                    avail={avail}
                    daySel={daySel}
                    onToggleDay={toggleDay}
                    onSelectDay={selectDay}
                    onToggleSlot={toggleSlot}
                    tempPassword={tempPassword}
                    onPasswordCleared={() => setTempPassword('')}
                  />
                ) : (
                  <ProfileView
                    biz={biz}
                    onEdit={() => { setEdit(true); setMsg(''); setFE(''); }}
                    imageHandler={imageHandler}
                    tempPassword={tempPassword}
                  />
                );
              case 'analytics': return <AnalyticsTab appts={appts} biz={biz} />;
              default: return null;
            }
          })()
        )}
      </main>
    </div>
  );
}

